import { useEffect, useRef, useState, useCallback } from "react";
import { sfx } from "../../lib/soundEngine";

const W = 340;
const H = 420;
const ROWS = 8;
const PEG_R = 3;
const BALL_R = 6;
const GRAVITY = 0.17;
const REST = 0.5;
const H_DAMP = 0.985;
const SLOTS = 9;
const MULTIPLIERS = [5, 2, 1.2, 0.5, 0.2, 0.5, 1.2, 2, 5];
const BETS = [5, 10, 25, 50, 100];
const DROP_MARGIN = 28;

const buildPegs = () => {
    const pegs = [];
    const startY = 60;
    const gapY = 34;
    const gapX = 32;
    for (let r = 0; r < ROWS; r++) {
        const count = 3 + r;
        const rowWidth = (count - 1) * gapX;
        const startX = (W - rowWidth) / 2;
        for (let i = 0; i < count; i++) {
            pegs.push({ x: startX + i * gapX, y: startY + r * gapY });
        }
    }
    return pegs;
};

const PEGS = buildPegs();
const SLOT_TOP = 60 + ROWS * 34 + 20;
const SLOT_W = W / SLOTS;

export const PlinkoGame = () => {
    const canvasRef = useRef(null);
    const rafRef = useRef(0);
    const ballsRef = useRef([]);
    const [bet, setBet] = useState(10);
    const [credits, setCredits] = useState(100);
    const [lastMultiplier, setLastMultiplier] = useState(null);
    const [best, setBest] = useState(100);
    const [busy, setBusy] = useState(false);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#0d0d18";
        ctx.fillRect(0, 0, W, H);

        // subtle grid
        ctx.strokeStyle = "rgba(43, 27, 84, 0.55)";
        ctx.lineWidth = 1;
        for (let x = 0; x <= W; x += 20) {
            ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
        }

        // pegs
        ctx.fillStyle = "#00f0ff";
        ctx.shadowColor = "#00f0ff";
        ctx.shadowBlur = 8;
        PEGS.forEach((p) => {
            ctx.beginPath();
            ctx.arc(p.x, p.y, PEG_R, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.shadowBlur = 0;

        // slots
        for (let i = 0; i < SLOTS; i++) {
            const m = MULTIPLIERS[i];
            const isHot = m >= 2;
            const color = isHot ? "#ff007f" : m >= 1 ? "#ffe600" : "#39ff14";
            ctx.strokeStyle = color;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(i * SLOT_W + 2, SLOT_TOP, SLOT_W - 4, H - SLOT_TOP - 4);
            ctx.fillStyle = color;
            ctx.shadowColor = color;
            ctx.shadowBlur = 6;
            ctx.font = "16px VT323, monospace";
            ctx.textAlign = "center";
            ctx.fillText(`${m}x`, i * SLOT_W + SLOT_W / 2, SLOT_TOP + 26);
        }
        ctx.shadowBlur = 0;

        // balls
        ballsRef.current.forEach((b) => {
            ctx.fillStyle = "#ffe600";
            ctx.shadowColor = "#ffe600";
            ctx.shadowBlur = 12;
            ctx.beginPath();
            ctx.arc(b.x, b.y, BALL_R, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.shadowBlur = 0;
    }, []);

    const step = useCallback(() => {
        const balls = ballsRef.current;
        for (let i = balls.length - 1; i >= 0; i--) {
            const b = balls[i];
            b.vy += GRAVITY;
            b.vx *= H_DAMP;
            b.x += b.vx;
            b.y += b.vy;
            // walls
            if (b.x < BALL_R) { b.x = BALL_R; b.vx = -b.vx * REST; }
            if (b.x > W - BALL_R) { b.x = W - BALL_R; b.vx = -b.vx * REST; }
            // pegs
            for (const p of PEGS) {
                const dx = b.x - p.x;
                const dy = b.y - p.y;
                const dist = Math.hypot(dx, dy);
                const min = PEG_R + BALL_R;
                if (dist < min) {
                    const nx = dx / (dist || 1);
                    const ny = dy / (dist || 1);
                    // push out
                    b.x = p.x + nx * min;
                    b.y = p.y + ny * min;
                    // reflect velocity
                    const dot = b.vx * nx + b.vy * ny;
                    b.vx = (b.vx - 2 * dot * nx) * REST;
                    b.vy = (b.vy - 2 * dot * ny) * REST;
                    // tiny random nudge to avoid symmetric stalls
                    b.vx += (Math.random() - 0.5) * 0.5;
                    if (!b.pinged || performance.now() - b.pinged > 60) {
                        sfx.tick();
                        b.pinged = performance.now();
                    }
                }
            }
            // slot bottom
            if (b.y > SLOT_TOP + 34) {
                const idx = Math.max(0, Math.min(SLOTS - 1, Math.floor(b.x / SLOT_W)));
                const mult = MULTIPLIERS[idx];
                const payout = Math.round(b.bet * mult);
                setLastMultiplier(mult);
                setCredits((c) => {
                    const next = c + payout;
                    setBest((prev) => (next > prev ? next : prev));
                    return next;
                });
                if (mult >= 2) sfx.win();
                else if (mult >= 1) sfx.match();
                else sfx.fail();
                balls.splice(i, 1);
            }
        }
        draw();
        if (balls.length > 0) {
            rafRef.current = requestAnimationFrame(step);
        } else {
            setBusy(false);
        }
    }, [draw]);

    useEffect(() => { draw(); }, [draw]);
    useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

    const drop = () => {
        if (credits < bet) {
            sfx.fail();
            return;
        }
        setCredits((c) => c - bet);
        setLastMultiplier(null);
        const dropX = DROP_MARGIN + Math.random() * (W - DROP_MARGIN * 2);
        ballsRef.current.push({
            x: dropX,
            y: 10,
            vx: (Math.random() - 0.5) * 0.6,
            vy: 0,
            bet,
            pinged: 0,
        });
        sfx.coin();
        if (!busy) {
            setBusy(true);
            rafRef.current = requestAnimationFrame(step);
        }
    };

    const reset = () => {
        ballsRef.current = [];
        setCredits(100);
        setBest((prev) => (prev < 100 ? 100 : prev));
        setLastMultiplier(null);
        sfx.coin();
        draw();
    };

    const cycleBet = (dir) => {
        setBet((b) => {
            const i = BETS.indexOf(b);
            const nextI = Math.max(0, Math.min(BETS.length - 1, i + dir));
            if (nextI !== i) sfx.click();
            return BETS[nextI];
        });
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between font-pixel text-lg">
                <span className="neon-amber" data-testid="plinko-credits">CREDITS: {credits}</span>
                <span className="neon-cyan" data-testid="plinko-best">BEST: {best}</span>
                <span
                    className={
                        lastMultiplier == null
                            ? "text-gray-500"
                            : lastMultiplier >= 2
                                ? "neon-pink"
                                : lastMultiplier >= 1
                                    ? "neon-amber"
                                    : "neon-lime"
                    }
                    data-testid="plinko-last"
                >
                    {lastMultiplier == null ? "—" : `LAST: ${lastMultiplier}x`}
                </span>
            </div>

            <canvas
                ref={canvasRef}
                width={W}
                height={H}
                data-testid="plinko-canvas"
                className="mx-auto rounded-md border-2"
                style={{
                    width: "100%",
                    maxWidth: W,
                    borderColor: "var(--neon-amber)",
                    boxShadow: "0 0 18px rgba(255, 230, 0, 0.3)",
                }}
            />

            {/* Bet stepper */}
            <div className="flex justify-center items-center gap-3">
                <span className="font-pixel text-sm text-gray-400 tracking-widest">BET</span>
                <button
                    data-testid="plinko-bet-down"
                    className="dpad-btn"
                    onClick={() => cycleBet(-1)}
                    aria-label="Lower bet"
                    disabled={bet === BETS[0]}
                    style={{ opacity: bet === BETS[0] ? 0.4 : 1 }}
                >
                    <i className="fa-solid fa-minus" />
                </button>
                <span
                    data-testid="plinko-bet-amount"
                    className="font-pixel text-2xl neon-amber min-w-[64px] text-center"
                >
                    {bet}
                </span>
                <button
                    data-testid="plinko-bet-up"
                    className="dpad-btn"
                    onClick={() => cycleBet(1)}
                    aria-label="Raise bet"
                    disabled={bet === BETS[BETS.length - 1]}
                    style={{ opacity: bet === BETS[BETS.length - 1] ? 0.4 : 1 }}
                >
                    <i className="fa-solid fa-plus" />
                </button>
            </div>

            <div className="flex justify-center items-center gap-3 flex-wrap">
                <button
                    onClick={drop}
                    data-testid="plinko-drop-btn"
                    disabled={credits < bet}
                    className="btn-neon btn-neon-amber"
                    style={{ opacity: credits < bet ? 0.5 : 1 }}
                >
                    <i className="fa-solid fa-arrow-down" /> Drop ({bet})
                </button>
                <button
                    onClick={reset}
                    data-testid="plinko-reset-btn"
                    className="btn-neon btn-neon-lime"
                >
                    <i className="fa-solid fa-rotate-right" /> Reset
                </button>
            </div>
            <p className="text-xs text-gray-500 text-center font-body">
                Each drop lands at a random column. Pick your bet, then send it. Pink slots pay 5×, amber 2× / 1.2×, green refunds.
            </p>
        </div>
    );
};
